import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Input, Button, Spinner, ZoneChip } from "../components/ui";
import { Search, ExternalLink } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

// Archive search: every listing the screen has ever seen, not just the 7-day
// window the other pages show. GET /api/archive/search (backend/routers/archive.py)
// answers from live_listings first, then archive_listings. Each result carries
// what lookup.py shows: its price history and the same flat under another id
// (twins). A pasted URL that matches nothing is read once server-side and its
// twins come back as url_lookup.

const eur = (v) => (v ? `€ ${Math.round(v).toLocaleString("de-AT")}` : "—");

// "2026-09-25" → "25.09.26"
const day = (iso) => (iso ? iso.slice(2, 10).split("-").reverse().join(".") : "—");

function SeenOrOnSite({ r, t }) {
  return r.on_site ? (
    <Link to={`/listings/${encodeURIComponent(r.id)}`} className="text-signal-green hover:underline">
      {t("archive.onSite")}
    </Link>
  ) : (
    <span data-testid="archive-gone" className="text-signal-yellow">
      {t("archive.lastSeen", { date: r.last_seen || "—" })}
    </span>
  );
}

function AdLink({ url, t }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
       className="inline-flex items-center gap-1 text-text-muted hover:text-white">
      <ExternalLink size={11} /> {t("archive.openAd")}
    </a>
  );
}

function Twins({ twins, t }) {
  if (!twins?.length) return null;
  return (
    <div className="mt-2 pl-3 border-l border-border">
      <div className="overline text-text-muted">{t("archive.twins")}</div>
      {twins.map((w) => (
        <div key={w.id} data-testid="archive-twin" className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono text-text-secondary">
          <span>{[w.platform, w.size_m2 ? `${w.size_m2} m²` : null, eur(w.price)].filter(Boolean).join(" · ")}</span>
          <SeenOrOnSite r={w} t={t} />
          <AdLink url={w.url} t={t} />
        </div>
      ))}
    </div>
  );
}

function UrlLookup({ lu, t }) {
  if (!lu) return null;
  let msg;
  if (lu.fetched) {
    const f = lu.facts || {};
    const facts = [f.title, eur(f.price_value), f.size_m2 ? `${f.size_m2} m²` : null,
                   f.postcode ? `PLZ ${f.postcode}` : null].filter(Boolean).join(" · ");
    msg = lu.twins?.length ? t("archive.urlRead", { facts }) : `${t("archive.urlRead", { facts })} ${t("archive.urlNoTwins")}`;
  } else {
    msg = lu.error === "not_portal" ? t("archive.urlNotPortal") : t("archive.urlFailed", { error: lu.error });
  }
  return (
    <div data-testid="archive-url-lookup" className="mt-6 border border-border px-4 py-3">
      <div className="text-xs text-text-secondary">{msg}</div>
      <Twins twins={lu.twins} t={t} />
    </div>
  );
}

const num = (v) => {
  const s = String(v ?? "").trim().replace(",", ".");
  return s === "" ? undefined : Number(s);
};

export default function Archive() {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [postcode, setPostcode] = useState("");
  const [minSize, setMinSize] = useState("");
  const [maxSize, setMaxSize] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  const filled = [q, postcode, minSize, maxSize, maxPrice].some((v) => String(v).trim());

  const run = async () => {
    if (!filled) return;
    setLoading(true);
    setErr(null);
    try {
      const params = {
        q: q.trim() || undefined,
        postcode: postcode.trim() || undefined,
        min_size: num(minSize),
        max_size: num(maxSize),
        max_price: num(maxPrice),
      };
      const { data } = await api.get("/archive/search", { params });
      setResult(data);
    } catch (e) {
      setErr(formatApiError(e));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") run();
  };

  const rows = result?.results || [];

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-6 border-b border-border bg-grid">
        <div className="max-w-3xl">
          <div className="overline">{t("archive.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("archive.title")}</h1>
          <p className="mt-2 text-xs text-text-muted max-w-2xl leading-relaxed">{t("archive.subtitle")}</p>
        </div>
      </div>

      <div className="p-6 max-w-5xl">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="archive-q"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("archive.placeholder")}
            className="flex-1"
          />
          <Button data-testid="archive-search" variant="green" onClick={run} disabled={loading || !filled}>
            {loading ? <Spinner size={14} /> : <Search size={14} />}
            {t("archive.searchButton")}
          </Button>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Input data-testid="archive-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)}
                 onKeyDown={onKeyDown} placeholder={t("archive.postcode")} inputMode="numeric" />
          <Input data-testid="archive-min-size" value={minSize} onChange={(e) => setMinSize(e.target.value)}
                 onKeyDown={onKeyDown} placeholder={t("archive.minSize")} inputMode="decimal" />
          <Input data-testid="archive-max-size" value={maxSize} onChange={(e) => setMaxSize(e.target.value)}
                 onKeyDown={onKeyDown} placeholder={t("archive.maxSize")} inputMode="decimal" />
          <Input data-testid="archive-max-price" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)}
                 onKeyDown={onKeyDown} placeholder={t("archive.maxPrice")} inputMode="numeric" />
        </div>
        <p className="mt-2 text-[11px] text-text-muted">{t("archive.twinHint")}</p>

        {err && (
          <div className="mt-4 border border-signal-red/40 bg-signal-red/10 px-4 py-3 text-sm text-signal-red">{err}</div>
        )}

        {result && result.archive_available === false && (
          <div data-testid="archive-unavailable" className="mt-4 border border-signal-yellow/40 bg-signal-yellow/10 px-4 py-3 text-xs text-signal-yellow">
            {t("archive.unavailable")}
          </div>
        )}

        {result && rows.length === 0 && !err && !result.url_lookup && (
          <div data-testid="archive-empty" className="mt-6 text-sm text-text-muted font-mono">{t("archive.noResults")}</div>
        )}

        {rows.length === 0 && <UrlLookup lu={result?.url_lookup} t={t} />}

        {rows.length > 0 && (
          <div className="mt-6 border border-border">
            <div className="px-4 py-2 border-b border-border overline text-text-muted">
              {t("archive.count", { n: rows.length })}{result.truncated ? ` · ${t("archive.truncated")}` : ""}
            </div>
            {rows.map((r) => (
              <div key={r.id} data-testid="archive-row" className="px-4 py-3 border-b border-border last:border-b-0">
                <div className="flex flex-wrap items-center gap-2">
                  <ZoneChip zone={r.zone} />
                  <span className="text-sm font-medium">{r.title || r.id}</span>
                </div>
                <div className="mt-1 text-xs font-mono text-text-secondary">
                  {[r.platform, r.market, r.postcode, r.size_m2 ? `${r.size_m2} m²` : null].filter(Boolean).join(" · ")}
                  {" · "}{eur(r.price)}
                  {r.first_price && r.price && r.first_price !== r.price ? (
                    <span className="text-text-muted"> ({t("archive.firstPrice")} {eur(r.first_price)})</span>
                  ) : null}
                  {" · "}Faktor {r.faktor ?? "—"}
                </div>
                {r.price_history?.length > 1 && (
                  <div data-testid="archive-history" className="mt-1 text-[11px] font-mono text-text-muted">
                    {t("archive.history")}:{" "}
                    {r.price_history.map(([d, p]) => `${eur(p)} (${day(d)})`).join(" → ")}
                  </div>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] font-mono">
                  <SeenOrOnSite r={r} t={t} />
                  <AdLink url={r.url} t={t} />
                </div>
                <Twins twins={r.twins} t={t} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
